import { useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { getEditAvailabilityPageUrl, getEditGamePageUrl, getFindPageUrl } from 'constants/pages';
import { useModal } from 'hooks/useModal';
import { useRequireAuth } from 'hooks/useRequireAuth';
import { useViewer } from 'hooks/useViewer';
import ArrowRight from 'svg/ArrowRight';
import IndividualGradient from 'svg/IndividualGradient';
import NoPeople from 'svg/NoPeople';
import PlusCircle from 'svg/PlusCircle';
import PlusPlain from 'svg/PlusPlain';
import {
  type HostPlayRequest,
  useMyRequestedAvailabilityIds,
} from 'screens/Find/components/RequestToPlay';
import { FIND_FETCH_RADIUS_MILES, type FindFilters } from 'screens/Find/filters';
import { searchAreas, searchCenterFor } from 'screens/Find/geo';
import { FindTab } from 'screens/Find/types';
import type { FindPlayer } from 'screens/Find/types';
import { useFindPlayers } from 'screens/Find/useFindPlayers';
import { useViewerCenter } from 'screens/Find/useViewerCenter';
import {
  HomeDateRangeEnum,
  HomeFilters,
} from 'screens/PlayerHomeV3/components/GetOnCourtSection/types';
import { feedAreas } from 'screens/PlayerHomeV3/components/TopOpportunitiesSection/sheetFilters';
import { ButtonLink } from 'components/Button';
import PlayerCard from 'components/Find/PlayerCard';
import FindPlayersSection, {
  type FindPlayersRequestSummary,
  useReceivedRequests,
} from 'components/FindPlayersSection';
import Skeleton from 'components/LoadingSkeleton';
import { selectTopPlayers } from './playerMatching';

const PlayerDetailsModal = dynamic(() => import('screens/Find/components/PlayerDetailsModal'));
const PlayerRequestModal = dynamic(
  () => import('screens/Find/components/RequestToPlay/PlayerRequestModal'),
);
const HostGameDetailModal = dynamic(
  () => import('screens/Find/components/RequestToPlay/HostGameDetailModal'),
);
const HostAvailabilityDetailModal = dynamic(
  () => import('screens/Find/components/RequestToPlay/HostAvailabilityDetailModal'),
);
const RequestReviewModal = dynamic(
  () => import('screens/Find/components/RequestToPlay/RequestReviewModal'),
);

const CARD_COUNT = 3;

interface Props {
  filters: HomeFilters;
  sheetFilters?: FindFilters;
}

export default function TopPlayerMatchesSection({ filters, sheetFilters }: Props) {
  const { userId } = useViewer();
  const requestedAvailabilityIds = useMyRequestedAvailabilityIds();
  // Anonymous viewers browse freely; requesting a game is where they log in.
  const { requireAuth, authModals, isAuthModalOpen } = useRequireAuth();
  const viewerCenter = useViewerCenter();

  const areas = useMemo(
    () => (sheetFilters ? feedAreas(sheetFilters) : searchAreas(FIND_FETCH_RADIUS_MILES, [])),
    [sheetFilters],
  );
  const searchCenter = useMemo(
    () => searchCenterFor(sheetFilters?.neighborhoods ?? [], viewerCenter),
    [sheetFilters?.neighborhoods, viewerCenter],
  );
  const { players: nearbyPlayers, isLoading } = useFindPlayers(viewerCenter, areas, searchCenter);
  // The viewer's own listings are neither shown nor counted.
  const allPlayers = useMemo(
    () => nearbyPlayers.filter((player) => !userId || player.targetUserId !== userId),
    [nearbyPlayers, userId],
  );
  // Availability listings only; bar filters + soonest-first ordering per playerMatching.
  // The count copy says "this week", so it ignores the Today narrowing.
  const { players, topPlayers } = useMemo(() => {
    const week = selectTopPlayers(
      allPlayers,
      { ...filters, dateRange: HomeDateRangeEnum.ThisWeek },
      sheetFilters,
    );
    const cards =
      filters.dateRange === HomeDateRangeEnum.Today
        ? selectTopPlayers(allPlayers, filters, sheetFilters)
        : week;
    return { players: week, topPlayers: cards.slice(0, CARD_COUNT) };
  }, [allPlayers, filters, sheetFilters]);

  const playerDetailModal = useModal();
  const playerRequestModal = useModal();
  const [selectedPlayer, setSelectedPlayer] = useState<FindPlayer | null>(null);
  const [requestPlayer, setRequestPlayer] = useState<FindPlayer | null>(null);

  const handleOpenDetails = (player: FindPlayer) => {
    setSelectedPlayer(player);
    playerDetailModal.openModal();
  };

  const handleRequestToPlay = (player: FindPlayer) =>
    requireAuth(() => {
      setRequestPlayer(player);
      playerRequestModal.openModal();
    });

  // Incoming requests on the viewer's own listings turn the banner into the
  // "Requests" card. Tapping one opens the host detail modal, which swaps to the
  // review modal (one modal at a time — the host modal hides while reviewing).
  const router = useRouter();
  const apolloClient = useApolloClient();
  const { requests: receivedRequests } = useReceivedRequests();
  const hostGameModal = useModal();
  const hostAvailabilityModal = useModal();
  const reviewModal = useModal();
  const [hostGameEventId, setHostGameEventId] = useState<string | null>(null);
  const [hostAvailabilityId, setHostAvailabilityId] = useState<string | null>(null);
  const [reviewRequest, setReviewRequest] = useState<HostPlayRequest | null>(null);

  const handleOpenRequests = (request: FindPlayersRequestSummary) => {
    if (request.type === 'GAME') {
      setHostGameEventId(request.id);
      hostGameModal.openModal();
    } else {
      setHostAvailabilityId(request.id);
      hostAvailabilityModal.openModal();
    }
  };

  const handleSeeRequest = (request: HostPlayRequest) => {
    setReviewRequest(request);
    reviewModal.openModal();
  };

  const handleRequestResolved = () => {
    apolloClient.refetchQueries({
      include: [
        'GetHostGame',
        'GetReceivedPlayRequests',
        'GetHostGameRequests',
        'GetHostAvailabilityRequests',
      ],
    });
  };

  return (
    <section className="container mx-auto mt-2 max-sm:-mt-ds-2xl sm:max-lg:-mt-12">
      <div className="my-ds-2xl h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode lg:hidden" />
      <div className="flex flex-col gap-ds-3xl max-lg:gap-ds-lg lg:rounded-[1.5rem] lg:border lg:border-color-border-input-lightmode lg:bg-color-bg-lightmode-primary lg:p-ds-3xl lg:shadow-[inset_0px_4px_16px_0px_rgba(0,0,0,0.08)] lg:dark:border-color-border-input-darkmode lg:dark:bg-color-bg-darkmode-primary">
        <div className="flex items-start justify-between gap-ds-lg max-lg:flex-col">
          <div className="flex flex-col gap-ds-md max-lg:gap-0">
            <div className="flex items-center gap-ds-2xl max-lg:gap-ds-md">
              <h2 className="max-lg:typography-informative-subheading-medium-highlight lg:typography-product-display text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Players near you
              </h2>
              <span className="flex items-center gap-ds-xs text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-lg:hidden">
                <IndividualGradient className="h-10 w-10 shrink-0" />
                <PlusPlain className="h-6 w-6 shrink-0" />
                <NoPeople className="h-10 w-10 shrink-0" />
              </span>
            </div>
            <p className="max-lg:typography-product-element-label lg:typography-product-button-label-large text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              <strong className="font-bold">{players.length}</strong> players are looking to play
              this week
            </p>
          </div>

          <ButtonLink
            href={getFindPageUrl(FindTab.Players)}
            variant="primary-light"
            size="md"
            isInline
            iconLeft={<img src="/images/app/individual.svg" alt="" className="h-5 w-5 shrink-0" />}
            iconRight={<ArrowRight className="h-4 w-4 shrink-0" />}
            className="shadow-[0_4px_20px_0_rgba(0,0,0,0.16)] max-lg:hidden"
          >
            View all players
          </ButtonLink>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-3 gap-ds-3xl max-lg:grid-cols-1 max-lg:gap-ds-lg">
            <Skeleton height="18rem" borderRadius="1.25rem" />
            <Skeleton height="18rem" borderRadius="1.25rem" className="max-lg:!hidden" />
            <Skeleton height="18rem" borderRadius="1.25rem" className="max-lg:!hidden" />
          </div>
        ) : topPlayers.length === 0 ? (
          // @todo empty-state design pending
          <p className="typography-product-text-card-desktop py-ds-xl text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            No players are looking for a partner nearby yet.
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-ds-3xl max-lg:grid-cols-1 max-lg:gap-ds-lg">
            {topPlayers.map((player) => (
              <PlayerCard
                key={player.id}
                player={player}
                onViewDetails={() => handleOpenDetails(player)}
                onRequestToPlay={() => handleRequestToPlay(player)}
                hasPendingRequest={requestedAvailabilityIds.has(player.id)}
              />
            ))}
          </div>
        )}

        <FindPlayersSection
          requests={receivedRequests}
          onOpenRequests={handleOpenRequests}
          withContainer={false}
          ctaLabel="List your availability"
          ctaIcon={<PlusCircle className="h-4 w-4 shrink-0 text-color-text-brand" />}
        />
      </div>

      <PlayerDetailsModal
        player={selectedPlayer}
        isOpen={
          playerDetailModal.isOpen &&
          !!selectedPlayer &&
          !playerRequestModal.isOpen &&
          !isAuthModalOpen
        }
        onClose={() => {
          playerDetailModal.closeModal();
          setSelectedPlayer(null);
        }}
        onRequestToPlay={() =>
          requireAuth(() => {
            setRequestPlayer(selectedPlayer);
            playerRequestModal.openModal();
          })
        }
      />

      <PlayerRequestModal
        player={requestPlayer}
        isOpen={playerRequestModal.isOpen}
        onClose={() => {
          playerRequestModal.closeModal();
          playerDetailModal.closeModal();
          setSelectedPlayer(null);
          setRequestPlayer(null);
        }}
      />

      <HostGameDetailModal
        eventId={hostGameEventId}
        isOpen={hostGameModal.isOpen && !reviewModal.isOpen}
        onClose={() => {
          hostGameModal.closeModal();
          setHostGameEventId(null);
        }}
        onSeeRequest={handleSeeRequest}
        onEdit={
          hostGameEventId ? () => router.push(getEditGamePageUrl(hostGameEventId)) : undefined
        }
      />

      <HostAvailabilityDetailModal
        availabilityId={hostAvailabilityId}
        isOpen={hostAvailabilityModal.isOpen && !reviewModal.isOpen}
        onClose={() => {
          hostAvailabilityModal.closeModal();
          setHostAvailabilityId(null);
        }}
        onSeeRequest={handleSeeRequest}
        onEdit={
          hostAvailabilityId
            ? () => router.push(getEditAvailabilityPageUrl(hostAvailabilityId))
            : undefined
        }
      />

      <RequestReviewModal
        request={reviewRequest}
        isOpen={reviewModal.isOpen}
        onClose={reviewModal.closeModal}
        onResolved={handleRequestResolved}
      />

      {authModals}
    </section>
  );
}
